import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { RepairOption, Device } from '../types'
import { useCartStore } from '../stores/cartStore'
import toast from 'react-hot-toast'

const RepairDetailPage: React.FC = () => {
  const { brandSlug, deviceSlug, repairSlug } = useParams<{
    brandSlug: string
    deviceSlug: string
    repairSlug: string
  }>()
  const navigate = useNavigate()
  const [repair, setRepair] = useState<RepairOption | null>(null)
  const [device, setDevice] = useState<Device | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [isExpress, setIsExpress] = useState(false)
  const { addItem } = useCartStore()

  useEffect(() => {
    const fetchRepairData = async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api'
        const response = await fetch(
          `${apiUrl}/repairs/by-url/${brandSlug}/${deviceSlug}/${repairSlug}`
        )
        const data = await response.json()

        if (!data.success) {
          setError('Reparatur nicht gefunden')
          return
        }

        setRepair(data.data)
        setDevice(data.data.deviceId as Device)
      } catch (err) {
        console.error('Error fetching repair:', err)
        setError('Fehler beim Laden der Reparaturdetails')
      } finally {
        setLoading(false)
      }
    }

    fetchRepairData()
  }, [brandSlug, deviceSlug, repairSlug])

  const handleAddToCart = () => {
    if (!repair || !device) return

    addItem({
      deviceId: device._id,
      repairId: repair._id,
      title: repair.title,
      price: isExpress
        ? Number((repair.price + repair.expressSurcharge).toFixed(2))
        : repair.price,
      isExpress,
      quantity: 1,
      device,
      repair
    })

    toast.success('Reparatur zum Warenkorb hinzugefügt!')
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Lade Reparaturdetails...</p>
        </div>
      </div>
    )
  }

  if (error || !repair || !device) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4 text-gray-900">Reparatur nicht gefunden</h2>
          <p className="text-gray-600 mb-8">{error || 'Die gesuchte Reparatur existiert nicht.'}</p>
          <button
            onClick={() => navigate('/repair')}
            className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition"
          >
            Zur Reparaturübersicht
          </button>
        </div>
      </div>
    )
  }

  // Schema.org Product structured data
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": repair.seoTitle || `${device.deviceModel} ${repair.title}`,
    "description": repair.seoDescription || repair.description,
    "image": device.image || '/images/devices/default.png',
    "brand": {
      "@type": "Brand",
      "name": device.brand
    },
    "offers": {
      "@type": "Offer",
      "price": repair.price,
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock",
      "url": `https://phone-medic.de/${brandSlug}/${deviceSlug}/${repairSlug}`,
      "priceValidUntil": new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "32"
    }
  }

  // Breadcrumb structured data
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://phone-medic.de"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": device.brand,
        "item": `https://phone-medic.de/${brandSlug}`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": device.deviceModel,
        "item": `https://phone-medic.de/${brandSlug}/${deviceSlug}`
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": repair.title,
        "item": `https://phone-medic.de/${brandSlug}/${deviceSlug}/${repairSlug}`
      }
    ]
  }

  // FAQ structured data
  const faqSchema = repair.faqItems?.length ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": repair.faqItems.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null

  const totalPrice = isExpress
    ? Number((repair.price + repair.expressSurcharge).toFixed(2))
    : repair.price

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{repair.seoTitle || `${device.deviceModel} ${repair.title} | PhoneMedic`}</title>
        <meta name="description" content={repair.seoDescription || repair.description} />
        {repair.seoKeywords && <meta name="keywords" content={repair.seoKeywords.join(', ')} />}

        {/* Open Graph */}
        <meta property="og:title" content={repair.seoTitle || `${device.deviceModel} ${repair.title}`} />
        <meta property="og:description" content={repair.seoDescription || repair.description} />
        <meta property="og:image" content={device.image || '/images/devices/default.png'} />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={`https://phone-medic.de/${brandSlug}/${deviceSlug}/${repairSlug}`} />

        {/* Canonical URL */}
        <link rel="canonical" href={`https://phone-medic.de/${brandSlug}/${deviceSlug}/${repairSlug}`} />

        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8 md:py-12">
        <div className="container mx-auto px-4 max-w-6xl">

          {/* Breadcrumbs */}
          <nav className="mb-6 md:mb-8 text-sm" aria-label="Breadcrumb">
            <ol className="flex items-center flex-wrap space-x-2 text-gray-600">
              <li><Link to="/" className="hover:text-indigo-600 transition">Home</Link></li>
              <li>/</li>
              <li><Link to="/repair" className="hover:text-indigo-600 transition">Reparaturen</Link></li>
              <li>/</li>
              <li><Link to={`/${brandSlug}/${deviceSlug}`} className="hover:text-indigo-600 transition">{device.deviceModel}</Link></li>
              <li>/</li>
              <li className="text-gray-900 font-medium">{repair.title}</li>
            </ol>
          </nav>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12">

            {/* Left Column: Image & Quick Facts */}
            <div>
              <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 sticky top-8">
                <img
                  src={device.image || '/images/devices/default.png'}
                  alt={`${device.deviceModel} ${repair.title}`}
                  className="w-full max-w-md mx-auto mb-6"
                />

                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="text-gray-600">Preis:</span>
                    <div className="text-right">
                      {repair.originalPrice && repair.originalPrice > repair.price && (
                        <span className="text-gray-400 line-through text-sm block">{repair.originalPrice.toFixed(2)}€</span>
                      )}
                      <span className="text-3xl font-bold text-indigo-600">{totalPrice.toFixed(2)}€</span>
                    </div>
                  </div>

                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="text-gray-600">Dauer:</span>
                    <span className="font-semibold">{repair.duration}</span>
                  </div>

                  {repair.warranty > 0 && (
                    <div className="flex justify-between items-center border-b pb-3">
                      <span className="text-gray-600">Garantie:</span>
                      <span className="font-semibold">{repair.warranty} Monate</span>
                    </div>
                  )}

                  {repair.expressAvailable && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <label className="flex items-center justify-between cursor-pointer">
                        <div>
                          <span className="font-medium block">Express-Service</span>
                          <span className="text-sm text-gray-600">Same-Day repair</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="text-sm font-semibold">+{repair.expressSurcharge.toFixed(2)}€</span>
                          <input
                            type="checkbox"
                            checked={isExpress}
                            onChange={(e) => setIsExpress(e.target.checked)}
                            className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                          />
                        </div>
                      </label>
                    </div>
                  )}

                  <button
                    onClick={handleAddToCart}
                    className="w-full bg-indigo-600 text-white py-4 rounded-lg font-bold text-lg hover:bg-indigo-700 transition shadow-lg hover:shadow-xl"
                  >
                    In den Warenkorb
                  </button>

                  <div className="text-center text-sm text-gray-500 space-y-1">
                    {repair.warranty > 0 && <p>✓ {repair.warranty} Monate Garantie</p>}
                    <p>✓ Versicherter Versand</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Content */}
            <div className="space-y-8">

              {/* H1 */}
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                {device.deviceModel} {repair.title}
              </h1>

              {/* Short Description */}
              <p className="text-lg text-gray-700 leading-relaxed">
                {repair.description}
              </p>

              {/* Long Description */}
              {repair.longDescription && (
                <div>
                  <h2 className="text-2xl font-bold mb-4 text-gray-900">Detaillierte Beschreibung</h2>
                  <div className="prose max-w-none text-gray-700 leading-relaxed whitespace-pre-line">
                    {repair.longDescription}
                  </div>
                </div>
              )}

              {/* Process Steps */}
              {repair.processSteps && repair.processSteps.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold mb-4 text-gray-900">So läuft die Reparatur ab</h2>
                  <ol className="space-y-4">
                    {repair.processSteps.map((step, idx) => (
                      <li key={idx} className="flex">
                        <span className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold mr-4">
                          {idx + 1}
                        </span>
                        <span className="pt-1 text-gray-700">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* MacBook Akku Video */}
              {brandSlug === 'macbook' && repairSlug === 'akku-austausch' && (
                <div>
                  <h2 className="text-2xl font-bold mb-4 text-gray-900">So läuft der Akkutausch ab</h2>
                  <div className="relative w-full rounded-xl overflow-hidden shadow-lg" style={{ paddingBottom: '56.25%' }}>
                    <iframe
                      className="absolute inset-0 w-full h-full"
                      src="https://www.youtube.com/embed/VuTgfnkQlcE"
                      title="MacBook Akkutausch – PhoneMedic"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </div>
              )}

              {/* FAQ */}
              {repair.faqItems && repair.faqItems.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold mb-4 text-gray-900">Häufig gestellte Fragen</h2>
                  <div className="space-y-4">
                    {repair.faqItems.map((faq, idx) => (
                      <details key={idx} className="bg-white rounded-lg shadow p-4 group">
                        <summary className="font-semibold cursor-pointer text-gray-900 list-none flex items-center justify-between">
                          {faq.question}
                          <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </summary>
                        <p className="mt-3 text-gray-700 leading-relaxed">{faq.answer}</p>
                      </details>
                    ))}
                  </div>
                </div>
              )}

            </div>
          </div>

        </div>
      </div>
    </>
  )
}

export default RepairDetailPage
